// The page-state helpers (slice 5): a loading pane, skeleton rows and a load error banner shared by every page.
// The page-level loading and error states the shell-level pages share (docs/UI-POLISH.md slice 5): skeleton rows in a pane
// while the first load runs, a danger banner with its icon when it fails. Nothing here changes what a page loads.
import type { ReactNode } from 'react';
import { AlertCircle } from './icons';

/** A pane of skeleton rows at row height: what a page shows while its first load runs. */
export function LoadingPane({ rows = 4, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div className="pane" role="status" aria-label={label}>
      <div className="skeleton-rows">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="skeleton-row">
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton rows without a frame, for the flush body of a pane that is already on screen. */
export function SkeletonRows({ rows = 3, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div className="skeleton-rows" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-row">
          <span className="skeleton" />
          <span className="skeleton" />
          <span className="skeleton" />
        </div>
      ))}
    </div>
  );
}

/** A failure (a load, a refused write): a danger banner with the tone icon and the message (with its request id). */
export function LoadError({ message, children }: { message?: ReactNode; children?: ReactNode }) {
  return (
    <div className="banner banner-danger" role="alert">
      <AlertCircle className="banner-icon" aria-hidden="true" />
      <div className="banner-body">
        {message}
        {children}
      </div>
    </div>
  );
}
